// Issues explain why a figure isn't ready yet and what the user can do next.
import type { TraceStep } from "../calc.ts";
import type { Dec } from "../money.ts";
import type { Id, Money } from "./types.ts";

export type Module = "section1256" | "propBusiness" | "selfEmployment" | "file";

/**
 * blocker: the figure can't be used until this is resolved.
 * review: the figure can be computed but shouldn't be filed until the user looks at this.
 * info: worth knowing; doesn't change readiness.
 */
export type Severity = "blocker" | "review" | "info";

export interface Issue {
  code: string;
  severity: Severity;
  module: Module;
  /** The person whose figure this affects; null for the household. */
  personId: Id | null;
  refs: Id[];
  message: string;
  nextAction: string;
}

/**
 * ready: every needed fact is confirmed.
 * provisional: computable, but something needs the user's review first.
 * blocked: something required is missing or conflicting. `partial` shows what is known so far and is never a filing figure.
 * not_applicable: this person has nothing for this module.
 */
export type ModuleResult<T> =
  | { state: "ready"; value: T; trace: TraceStep[]; issues: Issue[] }
  | { state: "provisional"; value: T; trace: TraceStep[]; issues: Issue[] }
  | { state: "blocked"; partial: T | null; trace: TraceStep[]; issues: Issue[] }
  | { state: "not_applicable"; issues: Issue[] };

export function resultFrom<T>(value: T | null, trace: TraceStep[], issues: Issue[]): ModuleResult<T> {
  if (value === null || issues.some((i) => i.severity === "blocker")) return { state: "blocked", partial: value, trace, issues };
  if (issues.some((i) => i.severity === "review")) return { state: "provisional", value, trace, issues };
  return { state: "ready", value, trace, issues };
}

export function m(v: Dec): Money {
  return v.toFixed();
}

export class IssueList {
  readonly items: Issue[] = [];
  add(issue: Issue) {
    const key = `${issue.code}|${issue.module}|${issue.personId}|${[...issue.refs].sort().join(",")}`;
    if (!this.items.some((i) => `${i.code}|${i.module}|${i.personId}|${[...i.refs].sort().join(",")}` === key)) this.items.push(issue);
  }
  for(module: Module, personId?: Id | null): Issue[] {
    return this.items.filter((i) => i.module === module && (personId === undefined || i.personId === null || i.personId === personId));
  }
}

/** A dollar amount for an issue message, like $1,234.56 or −$1,000.00. Trace values stay plain decimals. */
export function usd(v: Dec): string {
  const [int, frac] = v.abs().toFixed(2).split(".");
  const body = "$" + int!.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "." + frac;
  return v.isNeg() && !v.isZero() ? "−" + body : body;
}
